import { useState, useEffect } from "react";
import { Modal, Button, Dropdown } from "@lynx/ui-components";
import { useSelector } from "react-redux";
import cn from "classnames";

import constants from "lib/constants";
import { accountAPI } from "api/account-api";
import MarketDataSelection from "./MarketDataSelection";

import "./styles.scss";

const { RequestTypes } = constants;

const CreateSwitchPlatformModal = ({
  closeModal,
  updateCategory,
  categories,
  activeCategory,
  submitModal,
}) => {
  const { currentAccount } = useSelector((state) => state.accounts);
  const { marketdataProviders } = useSelector((state) => state.marketdata);
  const [platforms, setPlatforms] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);

  const defaultCategory = categories.find(
    (category) => category.value === activeCategory.value
  );

  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        setLoading(true);
        const response = await accountAPI.getSupportiveEntities("tradingplatforms");
        if (response.status === 200 && response.data.success) {
          const currentSoftware = currentAccount?.software;
          const currentSoftwareType = currentAccount?.softwareType;
          
          const availablePlatforms = response.data.tradingplatforms
            .filter((platform) => {
              // Filter out inactive platforms
              if (!platform.active) return false;
              
              // Filter out current platform (both software and softwareType must match)
              if (
                platform.software === currentSoftware &&
                platform.softwareType === currentSoftwareType
              ) {
                return false;
              }
              
              return true;
            })
            .map((platform) => ({
              label: `${platform.software} ${platform.softwareType}`,
              value: `${platform.software} ${platform.softwareType}`,
              software: platform.software,
              softwareType: platform.softwareType,
              platformData: platform,
            }));
          
          setPlatforms(availablePlatforms);
        }
      } catch (error) {
        console.error("Error fetching platforms:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlatforms();
  }, [currentAccount]);

  const handlePlatformChange = (value) => {
    const platform = platforms.find((p) => p.value === value);
    setSelectedPlatform(platform);
    // Reset selected markets when platform changes
    setSelectedMarkets([]);
    if (platform) {
      setShowError(false);
    }
  };

  const handleMarketsChange = (markets) => {
    setSelectedMarkets(markets);
  };

  const handleFormSubmit = () => {
    if (!selectedPlatform) {
      setShowError(true);
      return;
    }

    // Format market data for the request
    let marketData = [];
    if (selectedMarkets && selectedMarkets.length > 0 && marketdataProviders) {
      marketData = selectedMarkets
        .map((marketName) => {
          const foundMD = marketdataProviders.find((mdp) => mdp.name === marketName);
          if (foundMD) {
            return { group: foundMD.group, market: foundMD.name };
          }
          return null;
        })
        .filter((i) => i);
    }

    const body = {
      type: RequestTypes.SWITCH_PLATFORM,
      ext: {
        software: selectedPlatform.software,
        softwareType: selectedPlatform.softwareType,
        accountType: currentAccount?.accountType || "",
        marketData: marketData,
      },
    };

    submitModal({
      body,
      type: RequestTypes.SWITCH_PLATFORM,
    });
  };

  return (
    <div className="modal-window">
      <Modal
        className="switch-platform-modal cabinet-modals"
        overlayClassName="request-overlay"
        styleType="zimtra-primary"
        isOpen={true}
        hideBlur
        hasCloseIcon={false}
        onClickOutside={closeModal}
      >
        <Modal.Header
          className="modal-inner-header"
          onClose={closeModal}
          title={<h2 className="zimtra-h2">New Request</h2>}
        />
        <Modal.Body className="modal-inner-body">
          <div className="d-flex align-items-top modal-row first">
            <div className="left-side">Category:</div>
            <div>
              <Dropdown
                options={categories}
                defaultValue={defaultCategory.value}
                className="request-dropdown"
                placeholder="Select category"
                onChange={updateCategory}
              />
            </div>
          </div>
          <hr />
          <div className="d-flex align-items-top modal-row">
            <div className="left-side">Platform:</div>
            <div className={cn({ "error-field": showError && !selectedPlatform })}>
              <Dropdown
                options={platforms}
                className="request-dropdown"
                placeholder={loading ? "Loading platforms..." : "Select platform"}
                onChange={handlePlatformChange}
                disabled={loading}
              />
              {showError && !selectedPlatform ? (
                <div className="error-text">Please select a platform</div>
              ) : null}
            </div>
          </div>
          {selectedPlatform && (
            <>
              <hr />
              <div className="modal-row">
                <div className="left-side">Market Data:</div>
                <div className="marketdata-selection-wrapper">
                  <MarketDataSelection
                    selectedPlatform={selectedPlatform}
                    onMarketsChange={handleMarketsChange}
                    currentAccount={currentAccount}
                  />
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-inner-footer d-flex justify-content-center">
          <Button
            size="md"
            id="switch-platform-create-btn"
            buttonStyle="button-variant-second"
            buttonClassName="cabinet-primary-btn"
            disabled={!selectedPlatform || loading || !selectedMarkets || selectedMarkets.length === 0}
            onClick={handleFormSubmit}
            label="Create"
          />
          <Button
            size="md"
            id="switch-platform-cancel-btn"
            buttonStyle="button-variant-fourth"
            buttonClassName="cabinet-secondary-btn"
            onClick={closeModal}
            label="Cancel"
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CreateSwitchPlatformModal;

